import axios from "axios";
import Cookies from 'js-cookie';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';

import { MuiThemeProvider } from '@material-ui/core';
import { createTheme } from '@material-ui/core/styles';

import { BrightnessMedium, Close, EmojiEventsOutlined, HomeOutlined, ListAltOutlined, Menu } from '@material-ui/icons';

import Main from './Main';
import GlobalContext, { parseToken, applyTheme, getInitialTheme } from './global';
import './static/css/App.css';
import './static/css/Components.css';

import UserAvatar from './components/avatar';

const theme = createTheme({
  typography: {
    fontFamily: "PT-Root-UI",
    fontSize: 16,
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px",
    letterSpacing: "0em",
    textAlign: "left",
  }
});

class App extends React.Component {
  constructor(props) {
    super(props);

    const theme = getInitialTheme();
    applyTheme(theme);

    const user_data = Cookies.getJSON("user_data");
    if (user_data !== undefined) {
      const parsed_token = parseToken(user_data.token);
      this.state = {
        login_data: {login: user_data.login, role: parsed_token.role, uid: parsed_token.uid, token: user_data.token},
        theme: theme,
        avatar: "https://api.sort-me.org/getUserAvatar?id=" + parsed_token.uid,
      };
      axios.defaults.headers.common = { "Authorization": `Bearer ${this.state.login_data.token}`, }
    } else {
      this.state = {
        login_data: {login: null, role: null, uid: null, token: null,},
        theme: theme,
        avatar: null,
      }
    }
  }

  toggleTheme = () => {
    const new_theme = this.state.theme === "light" ? "dark" : "light";
    try {
      window.localStorage.setItem("theme", new_theme);
    } catch (error) {} // storage can be unavailable (private mode), the theme just won't persist
    applyTheme(new_theme, true);
    this.setState({theme: new_theme});
  }

  render() {
    const context = {
      ...this.state.login_data,
      theme: this.state.theme,
      avatar: this.state.avatar,
      toggleTheme: this.toggleTheme,
      setNewData: (new_login, new_avatar) => this.setState({login_data: {...this.state.login_data, login: new_login,}, avatar: new_avatar,}),
    };

    if (window.location.pathname === "/smolymp2022" && window.matchMedia("(max-width: 600px)").matches) {
      window.location.replace("https://t.me/sort_me/64");
      return null;
    }

    return (
      <GlobalContext.Provider value={context}>
        <MuiThemeProvider theme={theme}>
          <div className="app">
            <Navbar />
            <ScrollReset />
            <main className="page">
              <Main />
            </main>
          </div>
        </MuiThemeProvider>
      </GlobalContext.Provider>
    );
  }
}

// react-router never resets the scroll position: a newly opened page starts at the top.
// Back/forward (POP) keep the browser's restoration, search-only changes (?page=, ?show=) keep the position.
function ScrollReset() {
  const history = useHistory();

  useEffect(() => {
    let pathname = history.location.pathname;
    return history.listen((location, action) => {
      if (action === "PUSH" && location.pathname !== pathname) window.scrollTo(0, 0);
      pathname = location.pathname;
    });
  }, [history]);

  return null;
}

function Logo(props) {
  return <svg className="logo" width="101" height="19" viewBox="0 0 101 19" fill="var(--text)" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M7.31998 18.336C10.968 18.336 13.56 16.416 13.56 13.176C13.56 9.62402 10.704 8.73601 8.15998 7.96801C5.54398 7.17601 5.13598 6.64801 5.13598 5.88001C5.13598 5.20801 5.73598 4.60801 6.93598 4.60801C8.47198 4.60801 9.26398 5.35201 9.83998 6.55201L13.08 4.65601C11.856 2.18401 9.69598 0.864014 6.93598 0.864014C4.03198 0.864014 1.29598 2.73601 1.29598 5.97601C1.29598 9.19201 3.74398 10.392 6.23998 11.088C8.75998 11.784 9.71998 12.192 9.71998 13.224C9.71998 13.872 9.26398 14.592 7.43998 14.592C5.54398 14.592 4.51198 13.656 3.91198 12.264L0.599976 14.184C1.53598 16.656 3.79198 18.336 7.31998 18.336Z" />
    <path d="M23.7379 18.336C28.5619 18.336 32.4739 14.544 32.4739 9.60001C32.4739 4.65601 28.5619 0.864014 23.7379 0.864014C18.9138 0.864014 15.0019 4.65601 15.0019 9.60001C15.0019 14.544 18.9138 18.336 23.7379 18.336ZM23.7379 14.592C21.0019 14.592 18.8419 12.6 18.8419 9.60001C18.8419 6.60001 21.0019 4.60801 23.7379 4.60801C26.4739 4.60801 28.6339 6.60001 28.6339 9.60001C28.6339 12.6 26.4739 14.592 23.7379 14.592Z" />
    <path d="M43.8279 18H47.9559L44.3799 11.832C46.2279 10.896 47.4759 9.02401 47.4759 6.96001C47.4759 3.76801 44.9079 1.20001 41.7159 1.20001H34.9959V18H38.8359V12.48H40.6599L43.8279 18ZM38.8359 4.80001H41.7159C42.7719 4.80001 43.6359 5.73601 43.6359 6.96001C43.6359 8.18401 42.7719 9.12001 41.7159 9.12001H38.8359V4.80001Z" />
    <path d="M61.05 1.20001H48.57V4.89601H52.89V18H56.73V4.89601H61.05V1.20001Z" />
    <path d="M86.2509 1.20001H82.4109L77.8509 8.66401L73.2909 1.20001H69.4509V18H73.2909V8.23202L77.6349 15.36H78.0669L82.4109 8.23202V18H86.2509V1.20001Z" />
    <path d="M93.3384 14.304V11.328H99.3384V7.68002H93.3384V4.89601H99.9384V1.20001H89.4984V18H100.058V14.304H93.3384Z" />
  </svg>;
}

function Navbar(props) {
  const global_data = useContext(GlobalContext);
  const name = (global_data.login === undefined) ? "" : ((global_data.login === null) ? "Гость" : global_data.login);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <header className="navbar">
      <NavLink exact to="/" className="nav-logo" aria-label="Sort Me — на главную">
        <Logo />
      </NavLink>
      <nav className="nav-links">
        <NavLink activeClassName="active" to="/contest" className="nav-link">Соревнования</NavLink>
        <NavLink activeClassName="active" to="/tasks" className="nav-link">Задачи</NavLink>
      </nav>
      <div className="nav-actions">
        <Link to="/profile" className="nav-profile">
          <span id="profile-name">{name}</span>
          <UserAvatar src={global_data.avatar} size={36} id="nav-avatar" />
        </Link>
        <button type="button" id="theme-switch" onClick={global_data.toggleTheme} aria-label="Сменить тему" title="Сменить тему">
          <BrightnessMedium className="theme-switch-icon" />
        </button>
        <button type="button" className="nav-menu-button" onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Закрыть меню" : "Меню"} aria-expanded={menuOpen} aria-controls="nav-menu">
          {menuOpen ? <Close /> : <Menu />}
        </button>
      </div>
      {menuOpen && <NavMenu close={closeMenu} />}
    </header>
  );
}

// Phone menu (the hamburger in the top bar, see App.css), rendered only while open
function NavMenu({ close }) {
  const global_data = useContext(GlobalContext);
  const location = useLocation();
  const [pathname] = useState(location.pathname);

  // Navigating anywhere (a link here, Back) closes it
  useEffect(() => {
    if (location.pathname !== pathname) close();
  }, [location.pathname, pathname, close]);

  useEffect(() => {
    const phone = window.matchMedia("(max-width: 767px)");
    const onKey = event => event.key === "Escape" && close();
    const onResize = () => !phone.matches && close();
    document.documentElement.classList.add("scroll-locked");
    document.addEventListener("keydown", onKey);
    phone.addListener(onResize); // addListener: Safari < 14 has no addEventListener on MediaQueryList
    return () => {
      document.documentElement.classList.remove("scroll-locked");
      document.removeEventListener("keydown", onKey);
      phone.removeListener(onResize);
    };
  }, [close]);

  return (
    <div className="nav-menu" id="nav-menu" onClick={event => event.target === event.currentTarget && close()}>
      <nav className="nav-menu-panel" aria-label="Меню">
        <NavLink exact to="/" className="nav-menu-item" activeClassName="active" onClick={close}>
          <HomeOutlined />Главная
        </NavLink>
        <NavLink to="/contest" className="nav-menu-item" activeClassName="active" onClick={close}>
          <EmojiEventsOutlined />Соревнования
        </NavLink>
        <NavLink to="/tasks" className="nav-menu-item" activeClassName="active" onClick={close}>
          <ListAltOutlined />Задачи
        </NavLink>
        <NavLink to="/profile" className="nav-menu-item nav-menu-profile" activeClassName="active" onClick={close}
          // Only "my" profile is this item; someone else's profile is not
          isActive={(match, location) => location.pathname === "/profile" || location.pathname === "/profile/edit" || (global_data.uid != null && location.pathname === `/profile/${global_data.uid}`)}
        >
          <UserAvatar src={global_data.avatar} size={40} className="nav-menu-avatar" />
          <span>
            {global_data.login || "Гость"}
            <small>{global_data.login ? "Профиль" : "Войти или зарегистрироваться"}</small>
          </span>
        </NavLink>
      </nav>
    </div>
  );
}

export default App;
